import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { Check, X, ArrowLeft, ArrowUpRight, FileText } from "lucide-react";
import { services as staticServices, servicesList as staticList } from "@/data/services";
import api, { resolveMedia } from "@/lib/api";
import { Reveal } from "@/components/motion/Reveal";
import LeadPdfModal from "@/components/LeadPdfModal";
import useSEO from "@/lib/useSEO";

export default function ServizioDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(staticServices[slug] || null);
  const [list, setList] = useState(staticList);
  const [notFound, setNotFound] = useState(false);
  const [sample, setSample] = useState(null);
  const [leadOpen, setLeadOpen] = useState(false);

  useSEO({
    title: s ? s.title : "Servizio",
    description: s ? (s.short_pitch || s.subtitle || s.intro || "").slice(0, 155) : "",
  });

  useEffect(() => {
    if (!slug) return;
    setS(staticServices[slug] || null);
    setNotFound(false);
    api.get(`/services/${slug}`)
      .then((r) => setS(r.data))
      .catch((err) => {
        if (!staticServices[slug] && err.response?.status === 404) setNotFound(true);
      });
    api.get("/services").then((r) => { if (r.data?.length) setList(r.data); }).catch(() => {});
    api.get("/service-samples").then((r) => setSample(r.data[slug] || null)).catch(() => setSample(null));
  }, [slug]);

  if (notFound || (!s && notFound)) return <Navigate to="/servizi" replace />;
  if (!s) return null;

  const others = list.filter((x) => x.slug !== slug);

  return (
    <div data-testid={`page-servizio-${slug}`}>
      {/* Hero */}
      <section className="container-x pt-6 pb-16">
        <Link to="/servizi" className="inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-taupe hover:text-ink transition-colors mb-10" data-testid="back-to-servizi">
          <ArrowLeft size={14} /> Tutti i Servizi
        </Link>

        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-baseline gap-6">
              <span className="font-serif text-5xl text-gold">{s.number}</span>
              <span className="eyebrow">{s.tagline}</span>
            </div>
            <h1 className="font-serif text-5xl md:text-7xl leading-[1.03] mt-4">
              {s.title}
            </h1>
            <p className="mt-6 font-serif text-2xl md:text-3xl text-gold italic leading-snug">
              {s.subtitle}
            </p>
            {(s.highlights || []).length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2" data-testid={`servizio-highlights-${slug}`}>
                {s.highlights.map((h, i) => (
                  <span key={i} className="text-[11px] tracking-[0.1em] uppercase border border-gold/50 text-gold px-3 py-1.5" data-testid={`servizio-highlight-${slug}-${i}`}>
                    {h}
                  </span>
                ))}
              </div>
            )}
            {sample?.has_pdf && (
              <button
                onClick={() => setLeadOpen(true)}
                className="mt-8 inline-flex items-center gap-3 px-6 py-3 min-h-[48px] text-xs tracking-[0.14em] uppercase border border-ink hover:bg-ink hover:text-bone transition-colors"
                data-testid={`servizio-sample-${slug}`}
              >
                <FileText size={16} /> Ricevi un estratto del progetto
              </button>
            )}
          </div>
          <div className="lg:col-span-5">
            <img src={resolveMedia(s.hero_image)} alt={s.title} className="w-full h-[380px] object-cover" />
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="border-y border-hair bg-white py-20">
        <div className="container-x grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <span className="eyebrow">Il servizio</span>
            <h2 className="font-serif text-3xl md:text-4xl mt-4 leading-tight">
              Ecco come ti affiancherò.
            </h2>
          </div>
          <div className="md:col-span-8 md:pl-10">
            <p className="text-lg text-taupe leading-[1.9] font-light">{s.intro}</p>
          </div>
        </div>
      </section>

      {/* Doubts */}
      <section className="container-x py-20">
        <span className="eyebrow">Quali dubbi risolveremo insieme</span>
        <h2 className="font-serif text-3xl md:text-5xl mt-4 max-w-3xl leading-tight">
          Le domande da cui partiamo.
        </h2>
        <div className="grid md:grid-cols-3 gap-8 mt-12">
          {s.doubts.map((d, i) => (
            <Reveal key={i} delay={i * 0.1}>
            <div className="border-t border-ink pt-6">
              <div className="font-serif text-2xl">{d.title}</div>
              <p className="mt-4 text-sm text-taupe leading-relaxed font-light">{d.text}</p>
            </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Included / Excluded */}
      <section className="bg-white border-y border-hair py-20">
        <div className="container-x grid md:grid-cols-2 gap-12">
          <div>
            <span className="eyebrow text-gold">Cosa è incluso</span>
            <h3 className="font-serif text-3xl md:text-4xl mt-3">Cosa otterrai.</h3>
            <ul className="mt-8 space-y-4">
              {s.included.map((item, i) => (
                <li key={i} className="flex gap-3 text-ink">
                  <Check size={18} className="text-gold mt-1 shrink-0" strokeWidth={2} />
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <span className="eyebrow text-taupe">I nostri confini di qualità</span>
            <h3 className="font-serif text-3xl md:text-4xl mt-3">Cosa non è incluso.</h3>
            <ul className="mt-8 space-y-4">
              {s.excluded.map((item, i) => (
                <li key={i} className="flex gap-3 text-taupe">
                  <X size={18} className="text-ink/30 mt-1 shrink-0" strokeWidth={2} />
                  <span className="leading-relaxed font-light">{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-xs italic text-taupe">
              Tutto ciò che non è espressamente indicato in "cosa otterrai" è escluso dal servizio.
            </p>
            {s.direction_note && (
              <p className="mt-4 text-xs leading-relaxed text-taupe border border-hair bg-bone/60 p-4" data-testid="service-direction-note">
                {s.direction_note}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Timing & Payment */}
      <section className="container-x py-20">
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <span className="eyebrow">Tempistiche</span>
            <h3 className="font-serif text-3xl mt-3">Quanto tempo serve.</h3>
            <p className="mt-6 text-taupe leading-[1.9] font-light">{s.timing}</p>
          </div>
          <div>
            <span className="eyebrow">Modalità di pagamento</span>
            <h3 className="font-serif text-3xl mt-3">Come si struttura il compenso.</h3>
            <p className="mt-6 text-taupe leading-[1.9] font-light">{s.payment}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Carta di credito", "PayPal", "Bonifico bancario"].map((m) => (
                <span key={m} className="text-xs tracking-[0.14em] uppercase border border-hair px-3 py-1 text-taupe">
                  {m}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink text-bone py-20">
        <div className="container-x text-center">
          <span className="eyebrow text-gold">Iniziamo</span>
          <h2 className="font-serif text-4xl md:text-6xl mt-4 max-w-3xl mx-auto leading-tight text-bone">
            Pronto ad avviare {s.title}?
          </h2>
          <Link
            to="/prenota"
            className="mt-10 inline-flex items-center gap-3 px-10 py-4 text-sm tracking-[0.14em] uppercase border border-bone text-bone hover:bg-bone hover:text-ink transition-all"
            data-testid={`servizio-cta-${slug}`}
          >
            {s.cta}
          </Link>
        </div>
      </section>

      {/* Modal lead PDF */}
      {leadOpen && sample?.has_pdf && (
        <LeadPdfModal slug={slug} serviceTitle={s.title} sample={sample} onClose={() => setLeadOpen(false)} />
      )}

      {/* Other services */}
      <section className="container-x py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="eyebrow">Altri Percorsi</span>
            <h3 className="font-serif text-3xl md:text-4xl mt-3">Esplora anche</h3>
          </div>
          <Link to="/servizi" className="hidden md:inline-flex items-center gap-2 text-sm tracking-[0.14em] uppercase link-underline">
            Tutti i servizi <ArrowUpRight size={16} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {others.map((o) => (
            <div
              key={o.slug}
              role="link"
              tabIndex={0}
              onClick={() => { navigate(`/servizi/${o.slug}`); window.scrollTo(0, 0); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { navigate(`/servizi/${o.slug}`); window.scrollTo(0, 0); } }}
              className="group block border border-hair p-8 hover:border-gold transition-colors bg-white cursor-pointer select-none"
              data-testid={`altri-percorsi-${o.slug}`}
            >
              <div className="font-serif text-3xl text-gold">{o.number}</div>
              <div className="mt-8 eyebrow text-taupe">{o.tagline}</div>
              <div className="mt-2 font-serif text-2xl">{o.title}</div>
              <div className="mt-6 text-xs tracking-[0.14em] uppercase text-ink group-hover:text-gold transition-colors">Scopri →</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
